import React from 'react';
import { twMerge } from 'tailwind-merge';

export interface RadioGroupOption<T> {
  value: T;
  id?: string;
  disabled?: boolean;
  label: React.ReactNode;
  description?: React.ReactNode;
  additionalAction?: React.ReactNode;
}

export interface RadioGroupProps<T> {
  name: string;
  value?: T | null;
  disabled?: boolean;
  className?: string;
  onChange: (value: T) => void;
  options: RadioGroupOption<T>[];
}

function UpRadioGroup<T>(props: RadioGroupProps<T>) {
  const {
    name,
    value,
    options,
    onChange,
    className = '',
    disabled = false,
  } = props;

  return (
    <div
      role="radiogroup"
      className={twMerge(
        'flex flex-col *:border-up-black-20 *:border-b *:last:border-b-0 ',
        className,
      )}>
      {options.map(opt => {
        const checked = opt.value === value;
        const optId = String(opt.id ?? opt.value);
        const isDisabled = disabled || opt.disabled;

        return (
          <div key={optId} className="flex basis-full gap-x-2">
            <label
              htmlFor={optId}
              className={twMerge(
                'flex items-start gap-x-2 py-4 w-full rounded-lg select-none transition-shadow focus-within:shadow-outline',
                isDisabled ? 'cursor-not-allowed' : 'cursor-pointer',
              )}>
              <div className="flex-shrink-0 mt-0.5">
                <input
                  id={optId}
                  type="radio"
                  name={name}
                  checked={checked}
                  onChange={() => {
                    if (isDisabled) return;
                    onChange(opt.value);
                  }}
                  className="sr-only"
                  disabled={isDisabled}
                  aria-checked={checked}
                  aria-disabled={isDisabled}
                />
                <div
                  aria-hidden
                  className={twMerge(
                    'w-5 h-5 rounded-full border-2 flex items-center justify-center',
                    checked ? 'border-brand-highlight' : 'border-up-black-20',
                    isDisabled && 'opacity-50',
                  )}>
                  <div
                    className={twMerge(
                      'w-3 h-3 bg-brand-highlight rounded-full transform',
                      checked ? 'scale-100' : 'scale-0',
                    )}
                  />
                </div>
              </div>
              <div className="min-w-0">
                <div className="flex gap-x-2">
                  <div
                    className={twMerge('text-sm', isDisabled && 'opacity-50')}>
                    {opt.label ?? '-'}
                  </div>
                  {opt.disabled && (
                    <div className="text-[10px] flex items-center uppercase rounded font-semibold px-1 text-up-black-50 bg-up-black-10 leading-2.5">
                      Unserviceable
                    </div>
                  )}
                </div>
                {opt.description ? (
                  <div
                    className={twMerge(
                      'text-sm font-normal mt-2',
                      isDisabled && 'opacity-50',
                    )}>
                    {opt.description}
                  </div>
                ) : null}
              </div>
            </label>
            <div className="ml-auto py-4">{opt.additionalAction ?? null}</div>
          </div>
        );
      })}
    </div>
  );
}

export default UpRadioGroup;
